import { Box, Grid, Popover, ThemeProvider } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import TitleText from '../../components/formlib/TitleText';
import { ReactComponent as AcceptIcon } from '../../assets/images/acceptIcon.svg';
import { ReactComponent as BackIcon } from '../../assets/images/backarrow.svg';
import { ReactComponent as RejectIcon } from '../../assets/images/rejectIcon.svg';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ReactComponent as StatusIcon } from '../../assets/images/statusIcon.svg';
import { formatDate, getFiscalYearInfo } from '../../utils/dateUtil';
import { OptionType, ResponseType } from '../../utils/type';
import { useLocation, useNavigate } from 'react-router-dom';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import ModalPopup from '../../components/formlib/ModalPopup';
import { StatusType } from './ActivityTableRow';
import { ACTIVITY_APPROVAL_OVERVIEW_SHEET_ROUTE_NAME } from '../../routes/Routing';
import { useIntl } from 'react-intl';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import {
  ApiError,
  ApiMessage,
  isCustomError,
} from '../../services/ApiResponseHandler';
import WeekDatePicker from '../../components/formlib/modal/WeekDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import ProductivityChart from './ProductivityChart';
import { getStatusColor } from '../../utils/utilities';
import HolidayListDialog from '../holidays/HolidayListDialog';
import { Context } from '../../LanguageWrapper';
import { debounce } from 'lodash';
import { SELECTED_PROGRAM_KEY } from '../../services/Constant';
import {
  Holiday,
  getHolidayList,
} from '../../services/configApi/employees/holidayServices';
import {
  CustomTab,
  CustomTabs,
  TabTheme,
} from '../../components/formlib/CustomTabs';
import { TabPanel } from '../../components/formlib/TabPanel';
import BillableHoursView from './BillableHoursView';
import PayrollHoursView from './PayrollHoursView';
import {
  Activity,
  ActivitySheet,
  ActivitySubmitRequest,
  Appointment,
  getReviewBillingDetails,
  getReviewPayrollDetails,
  onHoldActivitySheetVisit,
  submitApprovedActivitySheet,
  submitActivitySheetByReviewer,
  submitReviewedPayrollActivities,
  GroupAppointment,
} from '../../services/configApi/activitySheet/weeklyActivitySheetProvider';
import ActivitySheetWeekPeekNew from './ActivitySheetWeekPeekNew';
import { ActivitySheetContextProvider } from '../../context/ActivitySheetContext';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import TextWithIcon from '../../components/formlib/TextWithIcon';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { ReactComponent as InfoIcon } from '../../assets/images/infoIcon.svg';
import TimeOffBalanceDialogue from './TimeOffBalanceDialogue';
import { TimeOutLogCardData } from '../profile/TimeOffBalancesCard';
import { getEmployeeTimeOffBalances } from '../../services/myTimeoff/MyTimeoffApi';

const ApprovalActivityScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const rctl = useIntl();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [activityDetails, setActivityDetails] = useState<ActivitySheet>();
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const [reload, setReload] = useState<boolean>(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [employeeName, setEmployeeName] = useState<string>('');
  const [successOrError, setSuccessOrError] = useState<ResponseType>('success');
  const [toastrId, setToastrId] = useState<string>();
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const context = useContext(Context);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [showHolidays, setShowHolidays] = useState<boolean>(false);
  const [needProductivity, setNeedProductivity] = useState<boolean>(false);
  const [confirmationPopup, setConfirmationPopup] = useState<boolean>(false);
  const [onHoldConfirmationPopup, setOnHoldConfirmationPopup] =
    useState<boolean>(false);
  const [onHoldVisit, setOnHoldVisit] = useState<{
    activitySheetId: string;
    visitId: string;
    isOnHold: boolean;
  } | null>(null);
  const [showTimeOffBalances, setShowTimeOffBalances] =
    useState<boolean>(false);
  const [tbbTimeOffBalances, setTbbTimeOffBalances] = useState<
    TimeOutLogCardData[]
  >([]);

  const handleReload = useCallback(() => {
    setReload((prev) => !prev);
    setActivityDetails(undefined);
  }, []);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // Add these helper variables at the top of the component
  const hasPayrollViewPermission = checkPermissionForFeature(
    'backend.payroll_hours_ras',
    'viewPermission',
  );
  const hasBillingViewPermission = checkPermissionForFeature(
    'backend.billing_hours_ras',
    'viewPermission',
  );
  const canBillingHoursEdit = checkPermissionForFeature(
    'backend.billing_hours_ras',
    'editPermission',
  );
  // Calculate dynamic tab indices
  const payrollTabIndex = hasPayrollViewPermission ? 0 : -1;
  let billingTabIndex: number;
  if (hasPayrollViewPermission && hasBillingViewPermission) {
    billingTabIndex = 1;
  } else if (hasBillingViewPermission) {
    billingTabIndex = 0;
  } else {
    billingTabIndex = -1;
  }
  const canShowSubmitActions =
    canBillingHoursEdit &&
    checkPermissionForFeature(
      'backend.submit_staff_activity_sheet',
      'editPermission',
    );
  const shouldShowSaveButton =
    canShowSubmitActions &&
    (activityDetails?.status === null ||
      activityDetails?.status === 'Rejected' ||
      activityDetails?.status === 'Not Submitted');
  const activities = activityDetails?.activities ?? [];

  const hasIncompleteProgressNotes = (activity: Activity): boolean => {
    if (activity.archived || activity.archivalStatus === 'Submitted') {
      return false;
    }

    return activity.groupBilling.some((group) =>
      group.appointments.some((appointment) => {
        if (
          appointment.archived ||
          appointment.attendeeRole !== 'CLINICIAN' ||
          appointment.onHold
        ) {
          return false;
        }
        return (
          appointment.progressNotesStatus === 'Pending' ||
          appointment.progressNotesStatus === 'In Progress'
        );
      }),
    );
  };

  const checkProgressNotesCompletion = (activityArray: Activity[]): boolean => {
    return !activityArray.some(hasIncompleteProgressNotes);
  };

  const hasActivitiesToSubmit = () => {
    const pendingActivities = activities.filter((activity) => {
      return (
        activity.status === null ||
        activity.status === 'Not Submitted' ||
        activity.status === 'Rejected'
      );
    });
    return pendingActivities.length > 0;
  };

  const convertGroupBillingDurations = (activity: Activity) => {
    for (const group of activity.groupBilling) {
      group.groupBillingDurationDec =
        group.groupBillingDurationDec === null
          ? null
          : group.groupBillingDurationDec;
    }
  };

  const isApptNotOnHold = (appt: Appointment): boolean => !appt.onHold;

  const filterGroupBilling = (activity: Activity) => {
    return activity.groupBilling
      .map((group) => ({
        ...group,
        appointments: group.appointments.filter(isApptNotOnHold),
      }))
      .filter((group) => {
        return group.appointments.some(
          (appt) => appt.status === null || appt.status === 'Rejected',
        );
      });
  };

  const collectProgressNotesIds = (activity: Activity): string[] => {
    const subIds: string[] = [];
    if (
      activity.status === null ||
      activity.status === 'Not Submitted' ||
      activity.status === 'Rejected'
    ) {
      for (const group of activity.groupBilling) {
        for (const appt of group.appointments) {
          if (isApptNotOnHold(appt)) {
            subIds.push(appt.progressNotesId);
          }
        }
      }
    }
    return subIds;
  };

  const shouldIncludeActivity = (activity: Activity): boolean => {
    return (
      (activity.archivalStatus === null ||
        activity.archivalStatus === 'Pending') &&
      (activity.status === 'Not Submitted' || activity.status === 'Rejected')
    );
  };

  const calculateWeekDates = (baseDate: Date) => {
    const currentDate = dayjs(baseDate);
    const newStartDate = currentDate.date(
      currentDate.date() - currentDate.day(),
    );
    const newEndDate = currentDate.date(
      currentDate.date() + (6 - currentDate.day()),
    );

    const startDateInUTC =
      moment(newStartDate.startOf('day').format(), 'YYYY-MM-DDTHH:mm:ss')
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';
    const endDateDateInUTC =
      moment(newEndDate.endOf('day').format(), 'YYYY-MM-DDTHH:mm:ss')
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';

    return { startDateInUTC, endDateDateInUTC };
  };

  const submitActivitySheet = useCallback(
    (activityArray: Activity[]) => {
      const submissionDate =
        selectedDate ??
        (activityDetails?.startDate
          ? new Date(activityDetails.startDate)
          : undefined);
      if (selectedEmployeeId === null || !submissionDate) {
        return;
      }

      const submitRequest: ActivitySubmitRequest = {
        employeeId: selectedEmployeeId,
        activities: [],
        startDate: '',
        endDate: '',
      };

      for (const activity of activityArray) {
        convertGroupBillingDurations(activity);

        if (shouldIncludeActivity(activity)) {
          const subIds = collectProgressNotesIds(activity);
          const filteredGroupBilling = filterGroupBilling(activity);

          submitRequest.activities.push({
            progressNotesIds: subIds,
            groupBilling: activity.archived ? [] : filteredGroupBilling,
            activitySheetId: activity.activitySheetId,
            status: activity.status,
          });
        }
      }

      if (submitRequest.activities.length === 0) {
        return;
      }

      const { startDateInUTC, endDateDateInUTC } =
        calculateWeekDates(submissionDate);
      submitRequest.startDate = startDateInUTC;
      submitRequest.endDate = endDateDateInUTC;

      toggleLoader(true);
      submitActivitySheetByReviewer(submitRequest)
        .then(() => {
          setSuccessOrError('success');
          setToastrId('activitySubmitSuccessMessage');
          setToastrDefaultMessage('Activity sheet submitted successfully');
          toggleLoader(false);
          handleReload();
        })
        .catch((error) => {
          toggleLoader(false);
          setSuccessOrError('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            const response = error as ApiMessage;
            if (response.code === 13001) {
              setToastrId('progressNoteNotSignedMessage');
              setToastrDefaultMessage(response.message);
            } else if (response.code === 13002) {
              setToastrId('timeOffNotApprovedMessage');
              setToastrDefaultMessage(response.message);
            } else if (response.code === 1057) {
              setToastrId('activitySheetGroupBillingErrorMessage');
              setToastrDefaultMessage(response.message);
            } else {
              setToastrId('activitySubmitFailureMessage');
              setToastrDefaultMessage('Failed to submit Activity sheet');
            }
          }
        });
    },
    [
      activityDetails?.startDate,
      selectedDate,
      toggleLoader,
      selectedEmployeeId,
    ],
  );

  const prepareActivityRequestAndSubmit = useCallback(
    (activityArray: Activity[]) => {
      submitActivitySheet(activityArray);
    },
    [submitActivitySheet],
  );

  const employeeSubmit = useMemo(
    () =>
      debounce((activityArray: Activity[]) => {
        prepareActivityRequestAndSubmit(activityArray);
      }, 300),
    [prepareActivityRequestAndSubmit],
  );

  useEffect(() => {
    return () => {
      employeeSubmit.cancel();
    };
  }, [employeeSubmit]);

  const handleSaveAction = () => {
    const progressNotesCompleted = checkProgressNotesCompletion(activities);

    if (!progressNotesCompleted) {
      setSuccessOrError('error');
      setToastrId('pendingProgressNoteValidationMessageReviewer');
      setToastrDefaultMessage(
        'One or more progress notes are not completed. Activity Sheet cannot be submitted.',
      );
      return;
    }

    const activitiesToSubmit = hasActivitiesToSubmit();
    if (activitiesToSubmit) {
      setConfirmationPopup(true);
    }
  };

  useEffect(() => {
    const navParams = location.state;
    if (navParams !== null) {
      setReload(true);
      setSelectedDate(navParams.date as Date);
      setSelectedEmployeeId(navParams.id);
      setEmployeeName(`${navParams.lastName}, ${navParams.firstName}`);
    }

    // Set default tab to the first available tab
    if (hasPayrollViewPermission || hasBillingViewPermission) {
      // Use dynamic tab index instead of hardcoded values
      // Determine the appropriate tab index based on permissions and params
      let selectedTabIndex = billingTabIndex;
      if (navParams?.reviewActivityTab === 'billing') {
        selectedTabIndex = billingTabIndex;
      } else if (payrollTabIndex >= 0) {
        selectedTabIndex = payrollTabIndex;
      }

      setTabIndex(selectedTabIndex);
    }
  }, []);

  useEffect(() => {
    if (selectedEmployeeId && selectedDate && reload) {
      if (tabIndex === payrollTabIndex) {
        readPayrollDetails();
      } else if (tabIndex === billingTabIndex) {
        readBillingDetails();
      }
      setReload(false);
    }
  }, [selectedEmployeeId, selectedDate, reload, tabIndex]);

  const updateNoteErrorMessage = (
    errorType: ResponseType,
    id: string,
    message: string,
  ) => {
    setSuccessOrError(errorType);
    setToastrId(id);
    setToastrDefaultMessage(message);
  };

  const onChevronClick = (expand: boolean, activitySheetId: string) => {
    if (activityDetails?.activities && activityDetails.activities.length > 0) {
      const updatedActivities = activityDetails.activities.map((activity) => {
        if (activity.activitySheetId === activitySheetId) {
          activity.expand = expand;
        }
        return activity;
      });
      setActivityDetails({ ...activityDetails, activities: updatedActivities });
    }
  };

  const updateAppointmentDescription = (
    appointments: Appointment[],
    progressNotesId: string,
    visitId: string,
    description: string,
  ): boolean => {
    for (const appointment of appointments) {
      if (
        appointment.progressNotesId === progressNotesId &&
        appointment.visitId === visitId
      ) {
        appointment.description = description;
        return true;
      }
    }
    return false;
  };

  const updateGroupBillingDescription = (
    activity: Activity,
    progressNotesId: string,
    visitId: string,
    description: string,
  ): boolean => {
    for (const group of activity.groupBilling) {
      if (
        updateAppointmentDescription(
          group.appointments,
          progressNotesId,
          visitId,
          description,
        )
      ) {
        return true;
      }
    }
    return false;
  };

  const updateActivityStatus = (
    activitySheetId: string,
    status: 'Open' | 'Closed' | 'Pending',
  ) => {
    if (activityDetails?.activities && activityDetails.activities.length > 0) {
      const updatedActivities = activityDetails.activities.map((activity) => {
        if (activity.activitySheetId === activitySheetId) {
          activity.activityStatus = status;
          if (status === 'Pending') {
            activity.status = 'Submitted' as StatusType;
          } else if (status === 'Open') {
            activity.status = 'Rejected' as StatusType;
          } else {
            activity.status = 'Approved' as StatusType;
          }
        }
        return activity;
      });
      setActivityDetails({ ...activityDetails, activities: updatedActivities });
    }
  };

  const updateVisitStatus = (
    activitySheetId: string,
    visitId: string,
    status: StatusType | null,
    updateParentStatus: boolean,
  ) => {
    if (activityDetails?.activities && activityDetails.activities.length > 0) {
      const updatedActivities = activityDetails.activities.map((activity) => {
        if (activity.activitySheetId === activitySheetId) {
          return {
            ...activity,
            groupBilling: activity.groupBilling.map((groupBilling) => ({
              ...groupBilling,
              appointments: groupBilling.appointments.map((appointment) =>
                appointment.visitId === visitId
                  ? { ...appointment, status }
                  : appointment,
              ),
            })),
          };
        }
        return activity;
      });

      for (const activity of updatedActivities) {
        if (activitySheetId === activity.activitySheetId) {
          const allStatus: Set<StatusType | null> = new Set();
          for (const groupBilling of activity.groupBilling) {
            const statuses = groupBilling.appointments.map((app) => {
              return app.status;
            });
            statuses.forEach((s) => allStatus.add(s));
          }
          if (updateParentStatus && !allStatus.has('Submitted')) {
            if (allStatus.has('Rejected')) {
              activity.activityStatus = 'Open';
              activity.status = 'Rejected' as StatusType;
            } else if (allStatus.has('Approved')) {
              activity.activityStatus = 'Closed';
              activity.status = 'Approved' as StatusType;
            } else {
              activity.activityStatus = 'Pending';
              activity.status = 'Submitted' as StatusType;
            }
            break;
          }
        }
      }

      setActivityDetails({
        ...activityDetails,
        activities: updatedActivities,
      });
    }
  };

  const updateVisitOnHold = (
    activitySheetId: string,
    visitId: string,
    onHold: boolean,
  ) => {
    if (!activitySheetId || !visitId || !onHold) {
      return;
    }
    setOnHoldVisit({ activitySheetId, visitId, isOnHold: onHold });
    setOnHoldConfirmationPopup(true);
  };

  const submitOnHoldVisit = () => {
    if (!onHoldVisit) {
      return;
    }

    toggleLoader(true);
    onHoldActivitySheetVisit(onHoldVisit.visitId, onHoldVisit.isOnHold)
      .then(() => {
        setSuccessOrError('success');
        setToastrId('onHoldSuccessMessage');
        setToastrDefaultMessage('Visit placed on-hold successfully');
        toggleLoader(false);
        setOnHoldConfirmationPopup(false);
        setOnHoldVisit(null);
        handleReload();
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('onHoldFailureMessage');
          setToastrDefaultMessage('Failed to place visit on-hold');
        }
        setOnHoldConfirmationPopup(false);
        setOnHoldVisit(null);
      });
  };

  const updateVisitLevelNote = (
    progressNotesId: string,
    visitId: string,
    description: string,
  ) => {
    if (activityDetails?.activities && activityDetails.activities.length > 0) {
      const updatedActivities = activityDetails.activities.map((activity) => {
        if (!activity.appointments) {
          return activity;
        }

        const updatedAppointment = updateAppointmentDescription(
          activity.appointments,
          progressNotesId,
          visitId,
          description,
        );

        if (!updatedAppointment) {
          updateGroupBillingDescription(
            activity,
            progressNotesId,
            visitId,
            description,
          );
        }
        return activity;
      });
      setActivityDetails({ ...activityDetails, activities: updatedActivities });
    }
  };

  const submitPayrollActivities = () => {
    if (selectedDate && selectedEmployeeId) {
      toggleLoader(true);
      submitReviewedPayrollActivities(
        dayjs(selectedDate).format('MM/DD/YYYY'),
        selectedEmployeeId,
      )
        .then(() => {
          setReload(true);
          toggleLoader(false);
          setSuccessOrError('success');
          setToastrId('payrollSubmitSuccessMessage');
          setToastrDefaultMessage('Payroll activities submitted successfully');
        })
        .catch((error) => {
          toggleLoader(false);
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
            setTabIndex(1);
          } else {
            setSuccessOrError('error');
            setToastrId('payrollSubmitFailureMessage');
            setToastrDefaultMessage('Failed to submit payroll activities');
          }
        });
    }
  };

  const submitReviewedBillableHours = () => {
    const submitRequest: ActivitySubmitRequest = {
      employeeId: selectedEmployeeId,
      activities: [],
      startDate: dayjs(selectedDate).format('MM/DD/YYYY'),
      endDate: '',
    };

    activityDetails?.activities?.forEach((activity) => {
      const subIds: string[] = [];
      const filteredGroupBilling = activity.groupBilling.filter(
        (gb) => gb.appointments.length > 0,
      );

      filteredGroupBilling.forEach((gb) => {
        gb.appointments.forEach((appt) => {
          if (
            (appt.archivalStatus === null ||
              appt.archivalStatus === 'Pending') &&
            !appt.archived
          ) {
            subIds.push(appt.progressNotesId);
          }
        });
      });

      submitRequest.activities.push({
        progressNotesIds: subIds,
        groupBilling: activity.archived ? [] : filteredGroupBilling,
        activitySheetId: activity.activitySheetId,
        status: activity.status,
      });
    });

    toggleLoader(true);
    submitApprovedActivitySheet(submitRequest)
      .then(() => {
        setSuccessOrError('success');
        setToastrId('activityApprovalSuccessMessage');
        setToastrDefaultMessage('Activity sheet reviewed successfully');
        toggleLoader(false);
        setTimeout(() => {
          navigate(ACTIVITY_APPROVAL_OVERVIEW_SHEET_ROUTE_NAME, {
            replace: true,
            state: {
              date: selectedDate,
            },
          });
        }, 1000);
      })
      .catch((error) => {
        toggleLoader(false);
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('activityApprovalFailureMessage');
          setToastrDefaultMessage('Failed to submit reviewed activity sheet');
          setSuccessOrError('error');
        }
      });
  };

  const getActivityStatus = (
    sheetStatus: StatusType | null,
  ): 'Open' | 'Closed' | 'Pending' => {
    if (sheetStatus === 'Approved') return 'Closed';
    if (sheetStatus === 'Rejected') return 'Open';
    return 'Pending';
  };

  const processAppointments = (appointments: Appointment[]) => {
    for (const appt of appointments) {
      appt.attendeeId = selectedEmployeeId;
      appt.originalStatus = appt.status;
    }
  };

  const processActivity = (
    activity: Activity,
    sheetStatus: StatusType | null,
  ) => {
    activity.activityStatus = getActivityStatus(sheetStatus);

    if (activity.appointments && activity.appointments.length > 0) {
      processAppointments(activity.appointments);
    } else {
      activity.originalStatus = activity.status;
    }
  };

  const processResponse = (response0: ActivitySheet) => {
    if (response0.activities) {
      for (const activity of response0.activities) {
        processActivity(activity, activity.status);
      }
    }
    return response0;
  };

  const viewProductivity = () => {
    setNeedProductivity(true);
    setReload(true);
  };

  const updateAppointmentsAttendeeId = (
    appointments: Appointment[] | null | undefined,
    attendeeId: string,
  ) => {
    appointments?.forEach((appt) => {
      appt.attendeeId = attendeeId;
    });
  };

  const normalizePayrollResponse = (response: ActivitySheet) => {
    response.activities?.forEach((activity) => {
      activity.expand = true;
      activity.originalStatus = activity.status;
      updateAppointmentsAttendeeId(activity.appointments, selectedEmployeeId);
    });
    return response;
  };

  const readPayrollDetails = () => {
    if (selectedEmployeeId === '' || !selectedDate) {
      return;
    }
    toggleLoader(true);
    getReviewPayrollDetails(
      formatDate(selectedDate, 'MM/DD/YYYY'),
      selectedEmployeeId,
      needProductivity,
    )
      .then(normalizePayrollResponse)
      .then(processResponse)
      .then((response: ActivitySheet) => {
        toggleLoader(false);
        // Process each activity's billing details
        response.activities?.forEach((activity) => {
          processActivityBillingHelper(activity);
        });

        const newResponse = structuredClone(formatAndReadResponse(response));

        setReload(false);
        setActivityDetails(newResponse);
      })
      .catch((error) => {
        setSuccessOrError('error');
        toggleLoader(false);
        if (isCustomError(error)) {
          setReload(false);
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          const response = error as ApiMessage;
          if (response.code === 1146) {
            setToastrId('employeeInactiveErrorMessage');
            setToastrDefaultMessage(
              'Employee is In-active for the selected week.',
            );
          } else {
            setReload(false);
          }
        }
      });
  };

  // Helper: Group appointments by appointmentId
  const groupAppointmentsByIdHelper = (
    appointments: Appointment[],
  ): Map<string, Appointment[]> => {
    const dayAppointments: Map<string, Appointment[]> = new Map();

    appointments.forEach((appt) => {
      const existing = dayAppointments.get(appt.appointmentId);
      if (existing) {
        existing.push(appt);
      } else {
        dayAppointments.set(appt.appointmentId, [appt]);
      }
    });

    return dayAppointments;
  };

  // Helper: Create base group billing object
  const createBaseGroupBillingHelper = (
    appts: Appointment[],
    isGroupBilling: boolean,
    hasInterpreter: boolean,
  ) => {
    const firstAppt = appts[0];
    const groupBilling: GroupAppointment = {
      isGroupBilling,
      apptTime: firstAppt.startTime ?? '',
      hasInterpreter,
      appointmentId: firstAppt.appointmentId,
      attendeeId: firstAppt.attendeeId ?? '',
      appointmentType: firstAppt.appointmentType,
      groupBillingStartTime: null,
      groupBillingDurationDec: null,
      appointments: appts,
    };
    return groupBilling;
  };

  // Helper: Create group billing object for multiple clients
  const createMultiClientGroupBillingHelper = (appts: Appointment[]) => {
    const firstAppt = appts[0];
    const hasInterpreter = appts.some(
      (appt) => appt.attendeeRole !== 'CLINICIAN',
    );
    const isGroupBilling = !hasInterpreter;

    const groupBilling: GroupAppointment = {
      isGroupBilling,
      apptTime: firstAppt.startTime ?? '',
      hasInterpreter,
      appointmentId: firstAppt.appointmentId,
      attendeeId: selectedEmployeeId ?? '',
      appointmentType: firstAppt.appointmentType,
      groupBillingStartTime: firstAppt.groupBillingStartTime,
      groupBillingDurationDec:
        firstAppt.groupBillingDurationDec === null
          ? null
          : firstAppt.groupBillingDurationDec,
      appointments: appts,
    };
    return groupBilling;
  };

  // Helper: Determine if appointments have single client
  const hasSingleClientHelper = (appts: Appointment[]): boolean => {
    const clientIds = new Set(appts.map((appt) => appt.clientId));
    return clientIds.size === 1;
  };

  // Helper: Process grouped appointments into group billing
  const processGroupedAppointmentsHelper = (
    groupedAppointments: Map<string, Appointment[]>,
  ) => {
    const groupBilling: GroupAppointment[] = [];

    for (const appts of groupedAppointments.values()) {
      if (appts.length === 1) {
        // Single appointment
        groupBilling.push(createBaseGroupBillingHelper(appts, false, false));
      } else if (hasSingleClientHelper(appts)) {
        // Multiple appointments, single client
        groupBilling.push(createBaseGroupBillingHelper(appts, false, false));
      } else {
        // Multiple appointments, multiple clients
        groupBilling.push(createMultiClientGroupBillingHelper(appts));
      }
    }

    return groupBilling;
  };

  // Helper: Process single activity
  const processActivityBillingHelper = (activity: Activity) => {
    if (!activity.appointments || activity.appointments.length === 0) {
      activity.groupBilling = [];
      return;
    }

    const groupedAppointments = groupAppointmentsByIdHelper(
      activity.appointments,
    );
    activity.appointments = [];
    activity.groupBilling =
      processGroupedAppointmentsHelper(groupedAppointments);
  };

  const readBillingDetails = () => {
    if (selectedEmployeeId === '' || !selectedDate) {
      return;
    }
    toggleLoader(true);
    getReviewBillingDetails(
      formatDate(selectedDate, 'MM/DD/YYYY'),
      selectedEmployeeId,
      needProductivity,
    )
      .then((response: ActivitySheet) => {
        response.activities?.forEach((activity) => {
          activity.expand = true;
          activity.originalStatus = activity.status;
          activity.appointments?.forEach((appt) => {
            appt.attendeeId = selectedEmployeeId;
            if (appt.status === null) {
              appt.status = 'Not Submitted';
            }
          });
        });
        return response;
      })
      .then(processResponse)
      .then((response: ActivitySheet) => {
        // Process each activity's billing details
        response.activities?.forEach((activity) => {
          processActivityBillingHelper(activity);
        });

        const newResponse = structuredClone(formatAndReadResponse(response));
        setActivityDetails(newResponse);
        toggleLoader(false);
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        }
      });
  };

  const formatAndReadResponse = (response: ActivitySheet) => {
    let rowId = 0;
    const newResponse: ActivitySheet = {
      ...response,
      activities:
        response.activities?.map((activity, index, arrayActivities) => {
          if (index === 0) {
            activity.rowId = rowId;
          } else {
            const previousActivity = arrayActivities[index - 1];
            if (previousActivity.day !== activity.day) {
              rowId = rowId === 0 ? 1 : 0;
            }
            activity.rowId = rowId;
          }
          return activity;
        }) ?? [],
    };
    newResponse.activities?.forEach((activity) => {
      if (activity.groupBilling.length > 0) {
        activity.groupBilling.forEach((gb) => {
          // Sort appointments based on clientLastName
          gb.appointments.sort((a, b) => {
            if (a.clientLastName < b.clientLastName) return -1;
            if (a.clientLastName > b.clientLastName) return 1;
            return 0;
          });
        });
        const gbLastIndex = activity.groupBilling.length - 1;
        const apptLastIndex =
          activity.groupBilling[gbLastIndex].appointments.length - 1;
        activity.groupBilling.forEach((gb, index) => {
          if (index === gbLastIndex) {
            gb.appointments.forEach((appt, i) => {
              appt.isLastIndex = i === apptLastIndex;
              return appt;
            });
          }
        });
      }
      return activity;
    });
    return newResponse;
  };

  const approveAllActivities = () => {
    if (activityDetails?.activities && activityDetails.activities.length > 0) {
      const updatedActivities = activityDetails.activities?.map((activity) => ({
        ...activity,
        status: 'Approved' as const,
        activityStatus: 'Closed' as const,
        groupBilling: activity.groupBilling.map((gb) => ({
          ...gb,
          appointments: gb.appointments.map((appt) => ({
            ...appt,
            status: appt.onHold ? appt.status : ('Approved' as const),
          })),
        })),
      }));
      setActivityDetails({ ...activityDetails, activities: updatedActivities });
    }
  };

  const rejectAllActivities = () => {
    if (activityDetails?.activities && activityDetails.activities.length > 0) {
      const updatedActivities = activityDetails.activities.map((activity) => ({
        ...activity,
        status: 'Rejected' as const,
        activityStatus: 'Open' as const,
        groupBilling: activity.groupBilling.map((gb) => ({
          ...gb,
          appointments: gb.appointments.map((appt) => ({
            ...appt,
            status: appt.onHold ? appt.status : ('Rejected' as const),
          })),
        })),
      }));
      setActivityDetails({ ...activityDetails, activities: updatedActivities });
    }
  };

  const readHolidayList = () => {
    const program = context.selectedProgram;
    if (!program || !selectedDate) {
      return;
    }
    toggleLoader(true);
    const { startYear, endYear } = getFiscalYearInfo(selectedDate);
    getHolidayList(startYear, endYear, program.id)
      .then((response) => {
        toggleLoader(false);
        setShowHolidays(true);
        setHolidays(response.holidayList);
      })
      .catch(() => {
        toggleLoader(false);
      });
  };

  const navigateToPreviousScreen = () => {
    navigate(ACTIVITY_APPROVAL_OVERVIEW_SHEET_ROUTE_NAME, {
      replace: true,
      state: {
        date: location.state?.date,
      },
    });
  };

  const checkForBulkAction = () => {
    return !(
      activityDetails?.status === null ||
      activityDetails?.status === 'Rejected' ||
      activityDetails?.status === 'Not Submitted'
    );
  };
  const fetchTbbTimeOffBalances = () => {
    if (!selectedDate) {
      return;
    }
    const { startYear, endYear } = getFiscalYearInfo(selectedDate);
    const employeeId = selectedEmployeeId;
    const selectedProgramStr = localStorage.getItem(SELECTED_PROGRAM_KEY);
    if (
      employeeId !== '' &&
      selectedProgramStr &&
      `07/01/${startYear}` &&
      `06/30/${endYear}`
    ) {
      const programId = (JSON.parse(selectedProgramStr) as OptionType).id;
      toggleLoader(true);
      getEmployeeTimeOffBalances(
        programId,
        employeeId,
        `07/01/${startYear}`,
        `06/30/${endYear}`,
      )
        .then((res) => {
          setTbbTimeOffBalances(res);
          setShowTimeOffBalances(true);
          toggleLoader(false);
        })
        .catch((error) => {
          toggleLoader(false);
          setSuccessOrError('error');

          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('failedProfileTimeoffMessage');
            setToastrDefaultMessage('Failed to load time off balances');
            setSuccessOrError('error');
          }
        });
    }
  };
  return (
    <Box
      component="main"
      sx={{
        width: '100%',
        backgroundColor: '#E7F0F0',
        padding: '16px',
      }}
    >
      {toastrId && (
        <SnackBarComponent
          open={toastrId !== undefined}
          handleClose={() => {
            setToastrId(undefined);
          }}
          labelId={toastrId}
          defaultMessageId={toastrDefaultMessage}
          successOrError={successOrError}
        />
      )}
      {showHolidays && (
        <HolidayListDialog
          open={showHolidays}
          date={selectedDate ?? new Date()}
          holidays={holidays}
          onClose={() => setShowHolidays(false)}
        />
      )}

      {confirmationPopup && (
        <ModalPopup
          open={confirmationPopup}
          maxWidth={'xs'}
          description="activitySheetConfirmationDescReviewer"
          onCancel={() => {
            setConfirmationPopup(false);
          }}
          onClose={() => {
            setConfirmationPopup(false);
          }}
          onOk={() => {
            setConfirmationPopup(false);
            employeeSubmit(activities);
          }}
          labelId1="cancelText"
          negativeActionLabel="Cancel"
          labelId2="Clientpage.Yesbtn"
          positiveActionLabel="Yes"
        />
      )}
      {onHoldConfirmationPopup && (
        <ModalPopup
          open={onHoldConfirmationPopup}
          maxWidth={'xs'}
          description={`${
            onHoldVisit && onHoldVisit.isOnHold
              ? 'onHoldVisitConfirmationDesc'
              : 'revertOnHoldVisitConfirmationDesc'
          }`}
          onCancel={() => {
            setOnHoldConfirmationPopup(false);
            setOnHoldVisit(null);
          }}
          onClose={() => {
            setOnHoldConfirmationPopup(false);
            setOnHoldVisit(null);
          }}
          onOk={() => {
            submitOnHoldVisit();
          }}
          labelId1="cancelText"
          negativeActionLabel="Cancel"
          labelId2="Clientpage.Yesbtn"
          positiveActionLabel="Yes"
        />
      )}
      <Box>
        <Box
          sx={{
            padding: '16px 0px',
            display: 'flex',
            flexDirection: 'row',
          }}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box className="flex__ mr-lg cursorPointer">
              <BackIcon onClick={() => navigateToPreviousScreen()} />
            </Box>
            <TitleText label={employeeName} />
            {activityDetails && (
              <Box
                sx={{
                  display: 'flex',
                  marginLeft: '16px',
                  alignItems: 'self-end',
                }}
                justifyContent={'center'}
              >
                <Box>
                  <MediumTypography
                    labelid={`${rctl.formatMessage({
                      id: 'statusText',
                      defaultMessage: 'Status',
                    })}:`}
                    sxProps={{ color: '#97A6A5' }}
                  />
                </Box>
                <Box className="activityStatusmt" sx={{ padding: '0px 5px' }}>
                  <StatusIcon fill={getStatusColor(activityDetails.status)} />
                </Box>
                <Box>
                  <MediumTypography
                    label={activityDetails.status ?? 'Not Submitted'}
                    sxProps={{
                      color: getStatusColor(activityDetails.status),
                      paddingLeft: '4px',
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
          {showTimeOffBalances && (
            <TimeOffBalanceDialogue
              open={showTimeOffBalances}
              tbbTimeOffBalances={tbbTimeOffBalances}
              onClose={() => setShowTimeOffBalances(false)}
            />
          )}

          {shouldShowSaveButton && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box className="flex__" justifyContent={'end'}>
                <Box
                  className="ml-xs"
                  onMouseEnter={handlePopoverOpen}
                  onMouseLeave={handlePopoverClose}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    marginRight: '8px',
                  }}
                >
                  <InfoIcon style={{ cursor: 'pointer' }} />
                  <Popover
                    id="mouse-over-popover"
                    sx={{
                      pointerEvents: 'none',
                    }}
                    open={open}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    onClose={handlePopoverClose}
                    disableRestoreFocus
                  >
                    <MediumTypography
                      className="p-sm"
                      labelid="activitySheetSubmissionInfo"
                      defaultlabel="Once you click Submit the system will submit this Activity Sheet for review"
                    />
                  </Popover>
                </Box>
                <ButtonComponent
                  className="btn-primary btn-submit"
                  variantType="contained"
                  type="button"
                  labelId={'submitText'}
                  defaultLabelId="Submit"
                  disabled={!activityDetails}
                  onClick={handleSaveAction}
                />
              </Box>
            </Box>
          )}
        </Box>

        <Grid container alignItems={'center'}>
          <Grid item>
            <Box
              sx={{ display: 'flex', marginRight: '16px' }}
              alignItems={'center'}
            >
              <WeekDatePicker
                maxDate={
                  activityDetails?.employeeEndDate
                    ? dayjs(activityDetails.employeeEndDate)
                    : dayjs().add(4, 'week').endOf('week')
                }
                date={dayjs(selectedDate)}
                onDateSelect={(newDate: Dayjs) => {
                  setSelectedDate(newDate.toDate());
                  setActivityDetails(undefined);
                  setReload(true);
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={6} display={'flex'}>
            <Box sx={{ display: 'flex' }}>
              <MediumTypography
                label={`${rctl.formatMessage({
                  id: 'employeeTypeText',
                  defaultMessage: 'Employee Type:',
                })} : `}
                sxProps={{ color: '#97A6A5' }}
              />
              <MediumTypography
                label={activityDetails?.employeeType ?? ''}
                defaultlabel="Hourly"
                sxProps={{ color: '#008C82', marginLeft: '8px' }}
              />
            </Box>
            {activityDetails?.employeeType &&
              activityDetails?.employeeType !== 'Salaried Exempt' && (
                <Box className="flex__ ml-lg alignItemCenter">
                  <MediumTypography
                    labelid={'lunchWaiverText'}
                    defaultlabel="Lunch Waiver"
                    sxProps={{ color: '#97A6A5' }}
                  />
                  <MediumTypography
                    label={':'}
                    className="mr-sm"
                    sxProps={{ color: '#97A6A5' }}
                  />
                  <MediumTypography
                    label={activityDetails?.lunchWaiverRequired ? 'Yes' : 'No'}
                    sxProps={{ color: '#008C82' }}
                  />
                </Box>
              )}
          </Grid>
          <Grid item sx={{ flex: 1, display: 'flex' }} justifyContent={'end'}>
            <Box
              sx={{
                alignSelf: 'center',
                marginRight: '16px',
              }}
              onClick={() => {
                fetchTbbTimeOffBalances();
              }}
            >
              <MediumTypography
                sxProps={{ color: '#008C82', cursor: 'pointer' }}
                labelid={`${rctl.formatMessage({
                  id: 'timeOutLogsText',
                  defaultMessage: 'Time Out Logs',
                })} ${dayjs().format('YYYY')}`}
                defaultlabel="Time Out Logs"
              />
            </Box>
            <Box
              sx={{
                alignSelf: 'center',
              }}
              onClick={() => {
                readHolidayList();
              }}
            >
              <MediumTypography
                sxProps={{ color: '#008C82', cursor: 'pointer' }}
                labelid={`${rctl.formatMessage({
                  id: 'holiday(s)Text',
                  defaultMessage: 'Holiday(s)',
                })} ${dayjs().format('YYYY')}`}
                defaultlabel="Holiday(s)"
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {activityDetails && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              marginTop: '16px',
            }}
          >
            <ActivitySheetWeekPeekNew
              employeeType={activityDetails.employeeType}
              activity={activityDetails}
              cardStyle={{
                width:
                  activityDetails.employeeType === 'Hourly Non-Exempt'
                    ? '100%'
                    : '60%',
                marginRight:
                  activityDetails.employeeType === 'Hourly Non-Exempt'
                    ? '0px'
                    : '8px',
              }}
            />

            {activityDetails.employeeType !== 'Hourly Non-Exempt' && (
              <ProductivityChart
                productivity={activityDetails.productivity}
                horizontalValue={activityDetails.productivityThreshold}
                cardStyle={{ width: '40%' }}
                barHeight="150px"
                viewProductivity={viewProductivity}
              />
            )}
          </Box>
        </Box>
      )}
      <ActivitySheetContextProvider
        updateVisitLevelNote={updateVisitLevelNote}
        updateNoteErrorMessage={updateNoteErrorMessage}
        activityStatus={activityDetails?.status ?? null}
        updateVisitStatus={updateVisitStatus}
        updateVisitOnHold={updateVisitOnHold}
        onChevronClick={onChevronClick}
        updateActivityStatus={updateActivityStatus}
        submitReviewedBillableHours={submitReviewedBillableHours}
        submitPayrollActivities={submitPayrollActivities}
        reload={handleReload}
        readPayrollDetails={readPayrollDetails}
        readBillingDetails={readBillingDetails}
      >
        <ThemeProvider theme={TabTheme}>
          <Box className="flex__">
            <Box
              sx={{
                flex: 1,
              }}
            >
              <CustomTabs
                className="mt-md"
                sx={{ marginLeft: '-12px' }}
                value={tabIndex}
                onChange={(_, index) => {
                  setTabIndex(index);
                  setReload(true);
                }}
                aria-label="review-activity-sheet-tabs"
              >
                {hasPayrollViewPermission && (
                  <CustomTab
                    label={rctl.formatMessage({
                      id: 'payrollHoursText',
                      defaultMessage: 'Payroll Hours',
                    })}
                  />
                )}
                {hasBillingViewPermission && (
                  <CustomTab
                    label={rctl.formatMessage({
                      id: 'billingHoursText',
                      defaultMessage: 'Billing Hours',
                    })}
                  />
                )}
              </CustomTabs>
            </Box>
            <Box
              sx={{
                flex: 1,
                alignContent: 'end',
              }}
            >
              <Box>
                <Box className="flex__" justifyContent={'end'}>
                  {checkPermissionForFeature(
                    'backend.billing_hours_ras',
                    'editPermission',
                  ) &&
                    checkForBulkAction() && (
                      <Box
                        sx={{
                          borderRadius: '4px',
                          border: '1px solid #008C82',
                          width: '250px',
                          display:
                            tabIndex === billingTabIndex ? 'flex' : 'none',
                        }}
                        justifyContent={'space-around'}
                        alignItems={'center'}
                      >
                        <TextWithIcon
                          LeftIcon={<AcceptIcon />}
                          labelId="acceptText"
                          defaultLabel="Approve All"
                          sxProps={{
                            cursor: 'pointer',
                            textTransform: 'none',
                            color: '#2A4241',
                          }}
                          onClick={() => {
                            approveAllActivities();
                          }}
                        />
                        <MediumTypography label="|" />
                        <TextWithIcon
                          LeftIcon={<RejectIcon />}
                          labelId="rejectText"
                          defaultLabel="Reject All"
                          sxProps={{
                            cursor: 'pointer',
                            textTransform: 'none',
                            color: '#2A4241',
                          }}
                          onClick={() => {
                            rejectAllActivities();
                          }}
                        />
                      </Box>
                    )}
                </Box>
              </Box>
            </Box>
          </Box>

          {hasPayrollViewPermission && (
            <TabPanel value={tabIndex} index={payrollTabIndex}>
              <PayrollHoursView
                selectedDate={selectedDate ?? new Date()}
                activityDetails={activityDetails}
                employeeName={employeeName}
                handleNoteUpdate={(activitySheetId, note) => {
                  if (activityDetails) {
                    const updatedActivities =
                      activityDetails.activities?.map((activity) => {
                        if (activity.activitySheetId === activitySheetId) {
                          activity.description = note;
                        }
                        return activity;
                      }) ?? [];
                    setActivityDetails({
                      ...activityDetails,
                      activities: updatedActivities,
                    });
                  }
                }}
              />
            </TabPanel>
          )}
          {hasBillingViewPermission && (
            <TabPanel value={tabIndex} index={billingTabIndex}>
              <BillableHoursView
                activityDetails={activityDetails}
                selectedDate={selectedDate ?? new Date()}
              />
            </TabPanel>
          )}
        </ThemeProvider>
      </ActivitySheetContextProvider>
    </Box>
  );
};

export default ApprovalActivityScreen;
